"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { Card, CardContent } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Spinner } from "@/components/ui/Spinner";
import { formatCurrency } from "@/lib/utils";

interface JewelryItem {
  id: string;
  sku: string;
  serialNumber: string | null;
  name: string;
  brand: string | null;
  category: string;
  retailPrice: number;
  salePrice: number | null;
  metalType: string | null;
  primaryStoneType: string | null;
  primaryStoneCarat: string | null;
  status: string;
  photoUrls: string[];
}

interface Customer {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  photoUrl: string | null;
  isVip: boolean;
  lifetimeValue: number;
  ringSize: string | null;
}

interface CartLine {
  id: string;
  item: JewelryItem;
  discount: number; // cents
}

type Tender = "CARD" | "CASH" | "FINANCING" | "WIRE" | "GIFT_CARD";

const TENDERS: Array<{ value: Tender; label: string; icon: string }> = [
  { value: "CARD", label: "Card", icon: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" },
  { value: "CASH", label: "Cash", icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" },
  { value: "FINANCING", label: "Financing", icon: "M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" },
  { value: "WIRE", label: "Wire", icon: "M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m9.032 4.026a9 9 0 010-8.716m0 0A9.004 9.004 0 0015.632 3m2.084 5.684L15.632 11 13.368 12.316 11 11" },
  { value: "GIFT_CARD", label: "Gift card", icon: "M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" },
];

function initials(a?: string | null, b?: string | null) {
  return [a?.[0], b?.[0]].filter(Boolean).join("").toUpperCase();
}

export function JewelryPOS() {
  // Item lookup
  const [itemQuery, setItemQuery] = useState("");
  const [items, setItems] = useState<JewelryItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const itemSearchRef = useRef<HTMLInputElement>(null);

  // Cart
  const [cart, setCart] = useState<CartLine[]>([]);
  const [taxRate] = useState(0.08875); // NYC rate demo

  // Customer
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerResults, setCustomerResults] = useState<Customer[]>([]);
  const [customerOpen, setCustomerOpen] = useState(false);

  // Transaction type / options
  const [transactionType, setTransactionType] = useState<"SALE" | "LAYAWAY" | "MEMO" | "REPAIR_TAKE_IN">("SALE");
  const [tender, setTender] = useState<Tender>("CARD");
  const [giftReceipt, setGiftReceipt] = useState(false);

  // Completion
  const [completed, setCompleted] = useState(false);
  const [completedNumber, setCompletedNumber] = useState("");

  // Fetch items on query change (debounced)
  useEffect(() => {
    const t = setTimeout(async () => {
      setItemsLoading(true);
      try {
        const params = new URLSearchParams();
        if (itemQuery) params.set("search", itemQuery);
        params.set("status", "IN_STOCK");
        const res = await fetch(`/api/jewelry-items?${params.toString()}`);
        const data = await res.json();
        setItems((data.data || []).slice(0, 16));
      } finally {
        setItemsLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [itemQuery]);

  // Fetch customers on query change
  useEffect(() => {
    if (!customerOpen) return;
    const t = setTimeout(async () => {
      const params = new URLSearchParams();
      if (customerQuery) params.set("search", customerQuery);
      params.set("limit", "8");
      try {
        const res = await fetch(`/api/customers?${params.toString()}`);
        const data = await res.json();
        setCustomerResults(data.data || []);
      } catch {
        // ignore
      }
    }, 200);
    return () => clearTimeout(t);
  }, [customerQuery, customerOpen]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        itemSearchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const addToCart = (item: JewelryItem) => {
    // Serialized — don't allow duplicates
    if (cart.find((c) => c.item.id === item.id)) return;
    setCart((prev) => [...prev, { id: `line-${item.id}-${Date.now()}`, item, discount: 0 }]);
    setItemQuery("");
  };

  const removeFromCart = (lineId: string) => {
    setCart((prev) => prev.filter((l) => l.id !== lineId));
  };

  const setDiscount = (lineId: string, discount: number) => {
    setCart((prev) => prev.map((l) => (l.id === lineId ? { ...l, discount } : l)));
  };

  const totals = useMemo(() => {
    const subtotal = cart.reduce((s, l) => s + (l.item.salePrice ?? l.item.retailPrice) - l.discount, 0);
    const tax = Math.round(subtotal * taxRate);
    const total = subtotal + tax;
    return { subtotal, tax, total };
  }, [cart, taxRate]);

  const completeTransaction = () => {
    const prefix = transactionType === "SALE" ? "ORD" : transactionType === "LAYAWAY" ? "LAY" : transactionType === "MEMO" ? "MEM" : "REP";
    const num = `${prefix}-2026-${String(Math.floor(1000 + Math.random() * 9000))}`;
    setCompletedNumber(num);
    setCompleted(true);
  };

  const resetTransaction = () => {
    setCart([]);
    setCustomer(null);
    setCustomerQuery("");
    setTransactionType("SALE");
    setTender("CARD");
    setGiftReceipt(false);
    setCompleted(false);
    setCompletedNumber("");
  };

  if (completed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black p-6">
        <Card className="w-full max-w-md border-emerald-300/20 bg-gradient-to-br from-emerald-950/40 to-black">
          <CardContent className="space-y-6 py-10 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-300/20">
              <svg className="h-8 w-8 text-emerald-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">
                {transactionType === "SALE" ? "Sale complete" : transactionType === "LAYAWAY" ? "Layaway opened" : transactionType === "MEMO" ? "Memo recorded" : "Repair logged"}
              </h2>
              <p className="mt-1 text-sm text-text-dim">Transaction {completedNumber}</p>
            </div>
            <div className="space-y-1 border-t border-white/10 pt-4 text-left">
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Subtotal</span>
                <span className="text-white">{formatCurrency(totals.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Tax</span>
                <span className="text-white">{formatCurrency(totals.tax)}</span>
              </div>
              <div className="flex justify-between border-t border-white/10 pt-2 text-base font-semibold">
                <span className="text-white">Total</span>
                <span className="text-white">{formatCurrency(totals.total)}</span>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={resetTransaction}
                className="w-full rounded-xl bg-white py-2.5 text-sm font-medium text-black transition hover:bg-white/90"
              >
                New transaction
              </button>
              <button
                type="button"
                onClick={() => window.print()}
                className="w-full rounded-xl border border-white/10 bg-white/5 py-2.5 text-sm font-medium text-white transition hover:bg-white/10"
              >
                Print receipt
              </button>
              {customer && (
                <Link
                  href={`/dashboard/customers/${customer.id}`}
                  className="w-full rounded-xl border border-white/10 bg-white/5 py-2.5 text-sm font-medium text-white transition hover:bg-white/10"
                >
                  View client profile
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid min-h-screen grid-cols-1 bg-black lg:grid-cols-[1fr_420px]">
      {/* LEFT: Item lookup + grid */}
      <div className="flex min-h-screen flex-col border-r border-white/5">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 border-b border-white/5 px-6 py-4">
          <div className="flex items-center gap-3">
            <Link
              href="/dashboard"
              aria-label="Back to dashboard"
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white/70 transition hover:bg-white/10 hover:text-white"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-amber-200 to-amber-400">
              <svg className="h-5 w-5 text-black" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2l3.5 6L22 9l-5 5 1.5 7L12 17.5 5.5 21 7 14 2 9l6.5-1L12 2z" />
              </svg>
            </div>
            <div>
              <h1 className="text-base font-semibold text-white">Point of Sale</h1>
              <p className="text-[11px] text-text-dim">Fifth Avenue Flagship</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(["SALE", "LAYAWAY", "MEMO", "REPAIR_TAKE_IN"] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTransactionType(t)}
                className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition ${
                  transactionType === t
                    ? "border-amber-200/40 bg-amber-200/10 text-amber-200"
                    : "border-white/10 bg-white/5 text-white/60 hover:border-white/20 hover:text-white"
                }`}
              >
                {t === "SALE" ? "Sale" : t === "LAYAWAY" ? "Layaway" : t === "MEMO" ? "Memo" : "Repair take-in"}
              </button>
            ))}
          </div>
        </div>

        {/* Search bar */}
        <div className="border-b border-white/5 p-6">
          <div className="relative">
            <svg
              className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-white/40"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              ref={itemSearchRef}
              value={itemQuery}
              onChange={(e) => setItemQuery(e.target.value)}
              placeholder="Scan barcode, enter SKU, serial, or search by stone/brand (⌘K)"
              className="w-full rounded-2xl border border-white/10 bg-white/5 py-4 pl-12 pr-4 text-base text-white placeholder:text-white/30 focus:border-amber-200/30 focus:outline-none focus:ring-2 focus:ring-amber-200/10"
            />
            <kbd className="absolute right-4 top-1/2 hidden -translate-y-1/2 rounded border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] font-medium text-white/40 lg:inline">
              ⌘K
            </kbd>
          </div>
        </div>

        {/* Item grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {itemsLoading ? (
            <div className="flex justify-center py-20"><Spinner /></div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center text-white/40">
              <svg className="h-12 w-12 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              <p className="mt-3 text-sm">No matching pieces</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {items.map((item) => {
                const inCart = cart.find((c) => c.item.id === item.id);
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => addToCart(item)}
                    disabled={!!inCart}
                    className={`group overflow-hidden rounded-2xl border transition ${
                      inCart
                        ? "border-amber-200/40 bg-amber-200/5 opacity-60"
                        : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10 active:scale-[0.98]"
                    }`}
                  >
                    <div className="relative aspect-square overflow-hidden bg-black/30">
                      {item.photoUrls?.[0] ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={item.photoUrls[0]}
                          alt={item.name}
                          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center text-white/20">
                          <svg className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                          </svg>
                        </div>
                      )}
                      {item.brand && (
                        <div className="absolute right-2 top-2 rounded-full bg-black/70 px-2 py-0.5 text-[10px] font-medium text-amber-200 backdrop-blur">
                          {item.brand}
                        </div>
                      )}
                      {inCart && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                          <div className="rounded-full border border-amber-200/40 bg-amber-200/20 px-3 py-1 text-xs font-medium text-amber-200">
                            In cart
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="space-y-1 p-3 text-left">
                      <p className="truncate text-xs font-semibold text-white">{item.name}</p>
                      <p className="text-[10px] text-text-dim">{item.sku}</p>
                      <div className="flex items-end justify-between">
                        <span className="text-xs text-white/70">
                          {item.primaryStoneCarat ? `${item.primaryStoneCarat}ct` : item.metalType?.replace(/_/g, " ")}
                        </span>
                        <span className="text-sm font-semibold text-white">{formatCurrency(item.salePrice ?? item.retailPrice)}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* RIGHT: Cart + Customer */}
      <div className="flex min-h-screen flex-col bg-gradient-to-b from-white/[0.02] to-transparent">
        {/* Customer panel */}
        <div className="border-b border-white/5 p-4">
          {!customer ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium uppercase tracking-wider text-text-dim">Attach client</p>
                <button
                  type="button"
                  onClick={() => {
                    setCustomerOpen(true);
                    setCustomerQuery("");
                  }}
                  className="text-xs font-medium text-amber-200 hover:text-amber-100"
                >
                  + Search
                </button>
              </div>
              {customerOpen && (
                <div className="space-y-2">
                  <input
                    autoFocus
                    value={customerQuery}
                    onChange={(e) => setCustomerQuery(e.target.value)}
                    placeholder="Search name, email, phone"
                    className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-white/30 focus:outline-none"
                  />
                  {customerResults.length > 0 && (
                    <div className="max-h-60 space-y-1 overflow-y-auto">
                      {customerResults.map((c) => {
                        const name = [c.firstName, c.lastName].filter(Boolean).join(" ");
                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => {
                              setCustomer(c);
                              setCustomerOpen(false);
                            }}
                            className="flex w-full items-center gap-3 rounded-lg border border-white/5 bg-white/5 px-3 py-2 text-left transition hover:border-white/10 hover:bg-white/10"
                          >
                            {c.photoUrl ? (
                              // eslint-disable-next-line @next/next/no-img-element
                              <img src={c.photoUrl} alt={name} className="h-8 w-8 rounded-full object-cover" />
                            ) : (
                              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-amber-200 to-amber-400 text-[10px] font-semibold text-black">
                                {initials(c.firstName, c.lastName) || "?"}
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="flex items-center gap-1.5 truncate text-sm font-medium text-white">
                                {name}
                                {c.isVip && <span className="rounded-full border border-amber-200/30 bg-amber-200/10 px-1.5 py-0 text-[9px] font-medium uppercase text-amber-200">VIP</span>}
                              </p>
                              <p className="truncate text-[10px] text-text-dim">{c.email || c.phone || "—"}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              {!customerOpen && (
                <button
                  type="button"
                  onClick={() => setCustomerOpen(true)}
                  className="flex w-full items-center gap-3 rounded-xl border border-dashed border-white/10 bg-white/[0.02] px-3 py-3 text-left transition hover:border-white/20"
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/5 text-white/40">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white/80">Walk-in</p>
                    <p className="text-[11px] text-text-dim">Tap to attach a client profile</p>
                  </div>
                </button>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3 rounded-xl border border-amber-200/20 bg-amber-200/5 p-3">
              {customer.photoUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={customer.photoUrl} alt="" className="h-10 w-10 rounded-full object-cover" />
              ) : (
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-amber-200 to-amber-400 text-xs font-semibold text-black">
                  {initials(customer.firstName, customer.lastName) || "?"}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1.5 truncate text-sm font-medium text-white">
                  {[customer.firstName, customer.lastName].filter(Boolean).join(" ")}
                  {customer.isVip && <Badge variant="warning" size="sm">VIP</Badge>}
                </p>
                <p className="truncate text-[11px] text-text-dim">
                  LTV {formatCurrency(customer.lifetimeValue)}
                  {customer.ringSize && ` · Ring ${customer.ringSize}`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setCustomer(null)}
                className="rounded-md p-1.5 text-white/40 transition hover:bg-white/10 hover:text-white"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Cart */}
        <div className="flex-1 overflow-y-auto">
          {cart.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center text-white/40">
              <svg className="h-12 w-12 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="text-sm">Scan a barcode or tap a piece</p>
              <p className="text-[11px] text-text-dim">Transactions are serialized by SKU</p>
            </div>
          ) : (
            <div className="space-y-2 p-4">
              {cart.map((line) => {
                const finalPrice = (line.item.salePrice ?? line.item.retailPrice) - line.discount;
                return (
                  <div key={line.id} className="rounded-xl border border-white/5 bg-white/5 p-3">
                    <div className="flex gap-3">
                      {line.item.photoUrls?.[0] ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={line.item.photoUrls[0]} alt={line.item.name} className="h-14 w-14 rounded-lg object-cover" />
                      ) : (
                        <div className="h-14 w-14 rounded-lg bg-white/5" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-white">{line.item.name}</p>
                        <p className="text-[11px] text-text-dim">
                          {line.item.sku}
                          {line.item.serialNumber && ` · S/N ${line.item.serialNumber}`}
                        </p>
                        <div className="mt-1.5 flex items-center justify-between">
                          <span className="text-sm font-semibold text-white">{formatCurrency(finalPrice)}</span>
                          <button
                            type="button"
                            onClick={() => removeFromCart(line.id)}
                            className="rounded p-1 text-white/40 hover:bg-white/10 hover:text-white"
                          >
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M10 7V4a1 1 0 011-1h2a1 1 0 011 1v3" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                    {/* Discount */}
                    <div className="mt-2 flex items-center gap-2 border-t border-white/5 pt-2">
                      <label className="text-[10px] uppercase tracking-wider text-text-dim">Discount</label>
                      <input
                        type="number"
                        min={0}
                        value={line.discount / 100 || ""}
                        onChange={(e) => setDiscount(line.id, Math.max(0, parseFloat(e.target.value || "0") * 100))}
                        className="w-20 rounded border border-white/10 bg-white/5 px-2 py-0.5 text-xs text-white focus:border-white/30 focus:outline-none"
                      />
                      {[50, 100, 250].map((d) => (
                        <button
                          key={d}
                          type="button"
                          onClick={() => setDiscount(line.id, d * 100)}
                          className="rounded bg-white/5 px-2 py-0.5 text-[10px] text-white/60 hover:bg-white/10 hover:text-white"
                        >
                          -${d}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Totals + Checkout */}
        <div className="space-y-3 border-t border-white/5 bg-black/30 p-4">
          <div className="space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="text-white/60">Subtotal</span>
              <span className="text-white">{formatCurrency(totals.subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-white/60">Tax ({(taxRate * 100).toFixed(3)}%)</span>
              <span className="text-white">{formatCurrency(totals.tax)}</span>
            </div>
            <div className="flex justify-between border-t border-white/10 pt-2 text-lg font-semibold">
              <span className="text-white">Total</span>
              <span className="text-white">{formatCurrency(totals.total)}</span>
            </div>
          </div>

          {/* Tender */}
          <div>
            <p className="mb-2 text-[10px] uppercase tracking-wider text-text-dim">Tender</p>
            <div className="grid grid-cols-5 gap-1.5">
              {TENDERS.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setTender(t.value)}
                  className={`flex flex-col items-center gap-1 rounded-lg border px-2 py-2 transition ${
                    tender === t.value
                      ? "border-amber-200/40 bg-amber-200/10 text-amber-200"
                      : "border-white/10 bg-white/5 text-white/60 hover:border-white/20 hover:text-white"
                  }`}
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={t.icon} />
                  </svg>
                  <span className="text-[9px] font-medium">{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Options */}
          <label className="flex items-center gap-2 text-xs text-white/70">
            <input
              type="checkbox"
              checked={giftReceipt}
              onChange={(e) => setGiftReceipt(e.target.checked)}
              className="h-4 w-4 rounded border-white/10 bg-white/5 text-amber-200 focus:ring-amber-200/20"
            />
            Gift receipt (no prices printed)
          </label>

          <button
            type="button"
            disabled={cart.length === 0}
            onClick={completeTransaction}
            className="w-full rounded-2xl bg-gradient-to-r from-amber-200 to-amber-400 py-4 text-base font-semibold text-black transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-30"
          >
            {transactionType === "SALE"
              ? `Charge ${formatCurrency(totals.total)}`
              : transactionType === "LAYAWAY"
              ? "Open layaway"
              : transactionType === "MEMO"
              ? "Send on memo"
              : "Take in for repair"}
          </button>
        </div>
      </div>
    </div>
  );
}
